<template>
  <span class="ai-insight-trigger">
    <el-button size="small" @click="open">业务分析</el-button>
    <el-button size="small" @click="openBackground">业务背景</el-button>
    <el-dialog title="业务分析" :visible.sync="visible" width="760px" append-to-body custom-class="ai-insight-dialog">
      <div v-loading="loading" class="ai-insight-body">
        <div v-if="!hasResult && !loading" class="empty">当前查询还没有分析结果。点右下角「开始分析」后，同样的查询条件会直接打开这份结果。</div>
        <template v-else-if="hasResult">
          <p class="summary">{{ summary }}</p>
          <div v-for="(item, index) in items" :key="index" class="card" :class="levelClass(item.level)">
            <div class="card-title">
              <span class="level">{{ item.level || "中" }}</span>
              <span class="problem">{{ item.problem }}</span>
            </div>
            <div v-if="item.evidence" class="block">
              <div class="label">依据</div>
              <p class="text">{{ item.evidence }}</p>
            </div>
            <div v-if="item.action" class="block">
              <div class="label">建议</div>
              <p class="text">{{ item.action }}</p>
            </div>
          </div>
        </template>
      </div>
      <span slot="footer">
        <el-button size="small" @click="visible = false">关闭</el-button>
        <el-button size="small" type="primary" :loading="loading" :disabled="!scene" @click="analyze">
          {{ hasResult ? "重新分析" : "开始分析" }}
        </el-button>
      </span>
    </el-dialog>
    <el-dialog title="业务背景" :visible.sync="backgroundVisible" width="640px" append-to-body :close-on-click-modal="false">
      <p class="background-tip">核心、留存、经营共用这段背景。分析时会带上这里的内容。改完后要点「重新分析」，才会按新背景生成。</p>
      <el-input v-model="background" type="textarea" :rows="12" placeholder="例如季节周期、哪些环比不能当成业务变差" />
      <span slot="footer">
        <el-button size="small" @click="backgroundVisible = false">取消</el-button>
        <el-button size="small" type="primary" :loading="backgroundSaving" @click="saveBackground">保存</el-button>
      </span>
    </el-dialog>
  </span>
</template>

<script>
import { getBizBackground, getBizInsight, saveBizBackground } from "@/api/data";

export default {
  data() {
    return {
      visible: false,
      loading: false,
      scene: "",
      facts: {},
      summary: "",
      items: [],
      cached: false,
      backgroundVisible: false,
      backgroundSaving: false,
      background: ""
    };
  },
  computed: {
    hasResult() {
      return !!this.summary || (this.items && this.items.length > 0);
    }
  },
  methods: {
    setContext(scene, facts) {
      this.scene = scene;
      this.facts = facts || {};
      this.summary = "";
      this.items = [];
      this.cached = false;
      if (this.visible) {
        this.lookup();
      }
    },
    open() {
      this.visible = true;
      this.lookup();
    },
    openBackground() {
      this.backgroundVisible = true;
      getBizBackground().then((res) => {
        this.background = res.data || "";
      });
    },
    saveBackground() {
      this.backgroundSaving = true;
      saveBizBackground({ content: this.background }).then((res) => {
        this.background = res.data || this.background;
        this.backgroundVisible = false;
        this.$message.success("已保存");
      }).finally(() => {
        this.backgroundSaving = false;
      });
    },
    lookup() {
      if (!this.scene) {
        return;
      }
      this.loading = true;
      getBizInsight({ scene: this.scene, facts: this.facts, refresh: false, _hideLoading: true }).then((res) => {
        this.apply(res.data || {});
      }).catch(() => {
        this.summary = "";
        this.items = [];
        this.cached = false;
      }).finally(() => {
        this.loading = false;
      });
    },
    analyze() {
      if (!this.scene) {
        return;
      }
      this.loading = true;
      getBizInsight({ scene: this.scene, facts: this.facts, refresh: true, _hideLoading: true }).then((res) => {
        this.apply(res.data || {});
      }).catch(() => {
        this.summary = "这次没有生成分析";
        this.items = [];
        this.cached = false;
      }).finally(() => {
        this.loading = false;
      });
    },
    apply(data) {
      this.summary = data.summary || "";
      this.items = data.items || [];
      this.cached = !!data.cached;
    },
    levelClass(level) {
      if (level === "高") {
        return "high";
      }
      if (level === "低") {
        return "low";
      }
      return "mid";
    }
  }
};
</script>

<style>
.ai-insight-dialog .el-dialog__body {
  padding: 8px 24px 4px;
}

.ai-insight-dialog .ai-insight-body {
  min-height: 180px;
  max-height: 62vh;
  overflow: auto;
}

.ai-insight-dialog .empty {
  padding: 28px 8px;
  color: #909399;
  font-size: 14px;
  line-height: 1.8;
}

.ai-insight-dialog .summary {
  margin: 0 0 16px;
  padding: 14px 16px;
  background: #f4f7fb;
  border-radius: 6px;
  color: #303133;
  font-size: 15px;
  line-height: 1.8;
}

.ai-insight-dialog .card {
  margin-bottom: 12px;
  padding: 14px 16px 6px;
  border: 1px solid #ebeef5;
  border-left-width: 3px;
  border-radius: 6px;
}

.ai-insight-dialog .card.high {
  border-left-color: #f56c6c;
}

.ai-insight-dialog .card.mid {
  border-left-color: #e6a23c;
}

.ai-insight-dialog .card.low {
  border-left-color: #909399;
}

.ai-insight-dialog .card-title {
  display: flex;
  align-items: flex-start;
  margin-bottom: 10px;
}

.ai-insight-dialog .level {
  flex: none;
  margin-right: 8px;
  padding: 0 6px;
  border-radius: 3px;
  color: #fff;
  font-size: 12px;
  line-height: 20px;
}

.ai-insight-dialog .high .level {
  background: #f56c6c;
}

.ai-insight-dialog .mid .level {
  background: #e6a23c;
}

.ai-insight-dialog .low .level {
  background: #909399;
}

.ai-insight-dialog .problem {
  color: #303133;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.6;
}

.ai-insight-dialog .block {
  margin-bottom: 10px;
}

.ai-insight-dialog .label {
  margin-bottom: 2px;
  color: #909399;
  font-size: 12px;
  line-height: 1.4;
}

.ai-insight-dialog .text {
  margin: 0;
  color: #606266;
  font-size: 14px;
  line-height: 1.8;
}

.background-tip {
  margin: 0 0 12px;
  color: #909399;
  font-size: 13px;
  line-height: 1.6;
}
</style>
